import { ResolvedConfig } from '../types';

/**
 * Generate UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create session ID
 */
function getSessionId(): string {
  const key = 'chat_widget_session_id';
  try {
    let sessionId = sessionStorage.getItem(key);
    if (!sessionId) {
      sessionId = generateUUID();
      sessionStorage.setItem(key, sessionId);
    }
    return sessionId;
  } catch {
    return generateUUID();
  }
}

/**
 * Get client timezone
 */
function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get URL query parameters
 */
function getQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  try {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  } catch {}
  return params;
}

/**
 * n8n Chat Payload Format
 */
interface N8nChatPayload {
  metadata: {
    clientCurrentDateTime: string;
    clientCurrentTimezone: string;
    clientQueryParams: Record<string, string>;
    clientUserAgent: string;
    userId?: string;
    userName?: string;
    userEmail?: string;
    [key: string]: unknown;
  };
  action: 'sendMessage' | 'loadPreviousSession';
  sessionId: string;
  chatInput: string;
}

/**
 * n8n Chat Response Format
 */
interface N8nChatResponse {
  output?: string;
  reply?: string;
  text?: string;
  message?: string;
  [key: string]: unknown;
}

/**
 * Chat API service for n8n backend communication
 */
export class ChatService {
  private config: ResolvedConfig;
  private sessionId: string;
  private history: Array<{ role: 'user' | 'assistant'; content: string }> = [];

  constructor(config: ResolvedConfig) {
    this.config = config;
    this.sessionId = getSessionId();
  }

  /**
   * Build the n8n payload
   */
  private buildPayload(message: string): N8nChatPayload {
    const metadata: N8nChatPayload['metadata'] = {
      clientCurrentDateTime: new Date().toString(),
      clientCurrentTimezone: getTimezone(),
      clientQueryParams: getQueryParams(),
      clientUserAgent: navigator.userAgent,
      // Include user info in metadata
      userId: this.config.user.id,
      userName: this.config.user.name || undefined,
      userEmail: this.config.user.email || undefined,
      // Spread custom metadata
      ...this.config.metadata
    };

    return {
      metadata,
      action: 'sendMessage',
      sessionId: this.sessionId,
      chatInput: message
    };
  }

  /**
   * Extract reply from various response formats
   */
  private extractReply(data: N8nChatResponse): string {
    // Support multiple response field names
    return data.output || data.reply || data.text || data.message || JSON.stringify(data);
  }

  /**
   * Send a message to the n8n backend API
   */
  async sendMessage(message: string): Promise<string> {
    const payload = this.buildPayload(message);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.config.apiKey) {
      headers['Authorization'] = `Bearer ${this.config.apiKey}`;
    }

    try {
      const response = await fetch(this.config.apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data: N8nChatResponse = await response.json();
      const reply = this.extractReply(data);

      // Update history
      this.history.push({ role: 'user', content: message });
      this.history.push({ role: 'assistant', content: reply });

      return reply;
    } catch (error) {
      console.error('[ChatWidget] API Error:', error);
      throw error;
    }
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Reset session (generates new session ID)
   */
  resetSession(): void {
    this.sessionId = generateUUID();
    try {
      sessionStorage.setItem('chat_widget_session_id', this.sessionId);
    } catch {}
    this.history = [];
  }

  /**
   * Clear conversation history
   */
  clearHistory(): void {
    this.history = [];
  }

  /**
   * Get current conversation history
   */
  getHistory(): Array<{ role: 'user' | 'assistant'; content: string }> {
    return this.history;
  }
}
